import * as Sentry from '@sentry/react'
import { useRouter } from '@tanstack/react-router'
import { type ReactNode, useEffect } from 'react'

// Initialize Sentry
if (typeof window !== 'undefined') {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE

  if (dsn) {
    Sentry.init({
      dsn,
      environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // Capture 10% of transactions in production
      // Session Replay
      replaysSessionSampleRate: 0.1, // Sample 10% of sessions
      replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors
      // Release tracking
      release: import.meta.env.VITE_APP_VERSION,
      // Don't send errors in development (optional)
      enabled: environment !== 'development',
    })
  }
}

export function SentryProvider({ children }: { children: ReactNode }) {
  const router = useRouter()

  useEffect(() => {
    if (!router) return

    // Track navigation as breadcrumbs
    const unsubscribe = router.subscribe('onLoad', ({ toLocation }) => {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: `Navigated to ${toLocation.pathname}`,
        level: 'info',
      })
    })

    return () => unsubscribe()
  }, [router])

  return <>{children}</>
}

// Error boundary wrapper
export const SentryErrorBoundary = Sentry.ErrorBoundary

// Convenience hooks and utilities
export { Sentry }

// Helper to capture errors with context
export const captureError = (error: Error, context?: Record<string, unknown>) => {
  if (context) {
    Sentry.setContext('error_context', context)
    // If request ID is in context, also set it as a tag for easier filtering
    if (context.requestId && typeof context.requestId === 'string') {
      Sentry.setTag('request_id', context.requestId)
    }
  }
  Sentry.captureException(error)
}

// Helper to set user context
export const setUser = (user: { id: string; email?: string; name?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  })
}

// Helper to clear user context (on logout)
export const clearUser = () => {
  Sentry.setUser(null)
}
